import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../Loader';
import Alert from '../Alert';
import serverClient from '../../api/ServerClient';
import { DeleteToken } from '../../auth/Auth';
import TransactionForm from '../Transactions/TransactionForm';
import DeleteTransactionModal from '../Transactions/DeleteTransactionModal';
import { useLanguage } from '../../context/LanguageContext';
import { getAccounts } from '../../util/cacheManager';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useSheetTouchIsolation from '../../hooks/useSheetTouchIsolation';

const UpdateTransactionBottomSheet = ({
    show,
    onClose,
    onTransactionUpdate,
    setShowToast,
    setShowToastForDelete,
    transaction,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { t } = useLanguage();

    const [accounts, setAccounts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [deletedTransactionId, setDeletedTransactionId] = useState(null);
    const overlayRef = useRef(null);

    useBodyScrollLock(show);
    useSheetTouchIsolation(overlayRef, show);

    const onTransactionDelete = (id) => {
        setDeletedTransactionId(id);
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // // Check cache for accounts first
                // const cachedAccounts = getAccountsFromCache();
                // const accountsData;
                //
                // if (Array.isArray(cachedAccounts) && cachedAccounts.length > 0) {
                //     // Use cached accounts
                //     accountsData = cachedAccounts;
                // } else {
                //     // Fetch from API if not in cache
                //     const accRes = await serverClient.get("/api/user/accounts");
                //     accountsData = accRes.data.accounts;
                //     // Store in cache for future use
                //     storeAccountsInCache(accountsData);
                // }

                const accountsData = await getAccounts();

                // Always fetch fresh categories
                const catRes = await serverClient.get('/api/user/categories');

                setAccounts(accountsData);
                setCategories(catRes.data.categories);
            } catch (e) {
                if (e.response?.status === 401) {
                    DeleteToken();
                    setError(t('userNotAuthorized'));
                    navigate('/login');
                } else {
                    setError(t('somethingWentWrong'));
                }
            } finally {
                setLoading(false);
            }
        };

        if (show) {
            fetchData();
        }
    }, [show, navigate, t]);

    const handleTransactionUpdate = (updated = 0) => {
        if (updated === 1) {
            setShowToastForDelete(true);
            setShowToast(false);
        } else {
            setShowToastForDelete(false);
            setShowToast(true);
        }
        onTransactionUpdate();
        onClose();
    };

    // Attachment upload/delete should refresh data immediately,
    // but keep the sheet open so user can continue editing.
    const handleAttachmentAction = () => {
        onTransactionUpdate();
    };

    return (
        <>
            <DeleteTransactionModal
                id={deletedTransactionId}
                onReturnFunc={handleTransactionUpdate}
                message={t('deleteTransactionConfirm')}
            />
            <Loader loading={loading} />
            <div
                ref={overlayRef}
                className={`bottom-sheet-overlay ${show ? 'open' : ''}`}
                onClick={onClose}
            >
                <div
                    className="auth-form bottom-sheet add-money-modal"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="sheet-header p-2">
                        <button className="close-btn dynamic-color-text" onClick={onClose}>
                            &times;
                        </button>
                        <h2 className="dynamic-color-text text-end">
                            {transaction?.transaction_type === 'transfer'
                                ? t('transferMoney')
                                : transaction?.transaction_type === 'expense'
                                  ? t('updateExpense')
                                  : transaction?.transaction_type === 'income'
                                    ? t('updateIncome')
                                    : transaction?.transaction_type === 'withdraw'
                                      ? t('withdrawMoney')
                                      : t('transaction')}
                        </h2>
                    </div>
                    <div className="scrollable-content">
                        <section className="section-b-space auth-form p-2">
                            {error && <Alert alert="danger">{error}</Alert>}
                            <TransactionForm
                                transaction={transaction}
                                accounts={accounts}
                                categories={categories}
                                onSuccess={handleTransactionUpdate}
                                onAttachmentAction={handleAttachmentAction}
                                setError={setError}
                                setLoading={setLoading}
                                onTransactionDelete={onTransactionDelete}
                            />
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
};

export default UpdateTransactionBottomSheet;
